import delay from '../utils/delay';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../utils/exceptionHandler.platform';
import makeLogger from '../utils/makeLogger';

const logger = makeLogger(__filename);

export type ExponentialBackoffLoaderOptions<TItem> = {
  requestedIds: string[];
  load: (ids: string[], isRetry: boolean) => Promise<{ [id: string]: TItem; }>;
  isItemValid?: (item: TItem) => boolean;
  maxAttempts?: number;
  initialDelayMs?: number;
  multiplier?: number;
  progressTracker?: { handleItemsLoaded: (items: { [id: string]: TItem; }) => void; };
};

export async function loadWithExponentialBackoff<TItem>(
  options: ExponentialBackoffLoaderOptions<TItem>,
): Promise<{ [id: string]: TItem; }> {
  const {
    requestedIds,
    load,
    isItemValid = () => true,
    maxAttempts = 10,
    initialDelayMs = 3000,
    multiplier = 1.3,
    progressTracker,
  } = options;

  if (requestedIds.length === 0) {
    return {};
  }

  const getIdsNeedingRetry = (requested: string[], loaded: { [id: string]: TItem; }) => {
    return requested.filter((id) => {
      const item = loaded[id];
      return !item || !isItemValid(item);
    });
  };

  let loadedItems = await load(requestedIds, false);
  let remainingIds = getIdsNeedingRetry(requestedIds, loadedItems);

  logger.debug('Initial items load finished', {
    requestedIds,
    loadedIds: Object.keys(loadedItems),
    remainingIds,
  });

  if (progressTracker && Object.keys(loadedItems).length > 0) {
    progressTracker.handleItemsLoaded(loadedItems);
  }

  let attempt = 0;
  while (remainingIds.length > 0 && attempt < maxAttempts) {
    const delayMs = Math.round(initialDelayMs * multiplier ** attempt);

    logger.debug(`Retrying ${remainingIds.length} items after ${delayMs}ms delay`, {
      remainingIds,
      attempt,
      delayMs,
    });

    await delay(delayMs);
    const reloadedItems = remainingIds.length > 0 ? await load(remainingIds, true) : {};
    loadedItems = {
      ...loadedItems,
      ...reloadedItems,
    };

    if (progressTracker && Object.keys(reloadedItems).length > 0) {
      progressTracker.handleItemsLoaded(reloadedItems);
    }

    remainingIds = getIdsNeedingRetry(requestedIds, loadedItems);
    attempt += 1;
  }

  if (remainingIds.length > 0) {
    exceptionHandler.captureException('Timed out loading items', {
      extra: {
        requestedIds,
        loadedIds: Object.keys(loadedItems),
        remainingIds,
        attempts: attempt,
      },
    });
  } else {
    logger.debug('Successfully loaded all items', {
      requestedIds,
      loadedIds: Object.keys(loadedItems),
    });
  }

  return loadedItems;
}

